export type CanvasImageExportFormat = 'png' | 'jpeg' | 'webp';
export type ImageEditorExportFormat = CanvasImageExportFormat | 'original';
export type ImageEditorCropMode = 'none' | 'free' | 'fixed-ratio' | 'fixed-size' | 'circular' | 'template';

export const imageEditorWebMaxDimension = 1920;

export interface ImageEditorDimensions
{
    width: number;
    height: number;
}

export interface ResolveExportDimensionsParams
{
    cropWidth: number;
    cropHeight: number;
    mode: ImageEditorCropMode;
    fixedSize: ImageEditorDimensions;
    circularSize: number;
    templateSize?: ImageEditorDimensions | null;
    outputResizeEnabled: boolean;
    outputSize: ImageEditorDimensions;
    optimizeDimensions: boolean;
    maxDimension: number;
}

export interface UploadImagePreprocessOptions
{
    enabled?: boolean;
    watermarkEnabled: boolean;
    watermarkType: 'text' | 'logo';
    watermarkText: string;
    watermarkLogoSrc?: string | null;
    watermarkX: number;
    watermarkY: number;
    watermarkOpacity: number;
    watermarkSize: number;
}

export interface WatermarkCenter
{
    x: number;
    y: number;
}

export function resolveWatermarkCenter(width: number, height: number, xPercent: number, yPercent: number, boxWidth: number, boxHeight: number)
{
    return {
        x: clamp((width * xPercent) / 100, boxWidth / 2, width - boxWidth / 2),
        y: clamp((height * yPercent) / 100, boxHeight / 2, height - boxHeight / 2)
    };
}

function clamp(value: number, min: number, max: number)
{
    if (min > max)
    {
        return (min + max) / 2;
    }

    return Math.max(min, Math.min(max, value));
}

export function getExportFormatInfo(format: CanvasImageExportFormat, quality?: number)
{
    if (format === 'jpeg')
    {
        return {
            extension: 'jpg',
            mimeType: 'image/jpeg',
            quality
        };
    }

    if (format === 'webp')
    {
        return {
            extension: 'webp',
            mimeType: 'image/webp',
            quality
        };
    }

    return {
        extension: 'png',
        mimeType: 'image/png',
        quality: undefined
    };
}

export function getEditorExportFormatInfo(format: ImageEditorExportFormat, quality?: number, originalName: string = '', contentType: string = '')
{
    if (format !== 'original')
    {
        return getExportFormatInfo(format, quality);
    }

    const extension = resolveOriginalExportExtension(originalName, contentType);
    const resolvedFormat = resolveOriginalExportFormat(extension);
    const info = getExportFormatInfo(resolvedFormat, quality);

    return {
        ...info,
        extension
    };
}

export function buildEditedImageFilename(originalName: string, format: ImageEditorExportFormat, prefix: string)
{
    const baseName = removeImageExtension(originalName || 'image');

    return buildImageEditorFilename(`${prefix}_${baseName}`, format, originalName);
}

export function buildImageEditorFilename(fileName: string, format: ImageEditorExportFormat, originalName: string = '', contentType: string = '')
{
    const extension = getEditorExportFormatInfo(format, 1, originalName, contentType).extension;
    const baseName = removeImageExtension((fileName || originalName || 'image').trim());

    return `${baseName || 'image'}.${extension}`;
}

export function buildBatchEditedImageFilename(originalName: string, format: ImageEditorExportFormat, pattern: string, index: number, contentType: string = '')
{
    const originalBaseName = removeImageExtension(originalName || 'image');
    const safePattern = pattern?.trim() || 'edited_{name}';
    const indexedPattern = safePattern.includes('{name}') || safePattern.includes('{index}')
        ? safePattern
        : `${safePattern}_{index}`;
    const fileName = indexedPattern
        .replaceAll('{name}', originalBaseName)
        .replaceAll('{index}', index.toString());

    return buildImageEditorFilename(fileName, format, originalName, contentType);
}

function removeImageExtension(fileName: string)
{
    return fileName.replace(/\.(jpe?g|png|webp)$/i, '');
}

export function resolveImageEditorExportFormat(format: ImageEditorExportFormat, originalName: string = '', contentType: string = '')
{
    if (format !== 'original')
    {
        return format;
    }

    return resolveOriginalExportFormat(resolveOriginalExportExtension(originalName, contentType));
}

function resolveOriginalExportExtension(originalName: string, contentType: string)
{
    const extension = originalName.toLowerCase().match(/\.([^/.]+)$/)?.[1];

    if (extension === 'jpg' || extension === 'jpeg' || extension === 'png' || extension === 'webp')
    {
        return extension;
    }

    if (contentType?.toLowerCase() === 'image/jpeg')
    {
        return 'jpg';
    }

    if (contentType?.toLowerCase() === 'image/webp')
    {
        return 'webp';
    }

    return 'png';
}

function resolveOriginalExportFormat(extension: CanvasImageExportFormat | 'jpg')
{
    if (extension === 'jpg')
    {
        return 'jpeg';
    }

    return extension;
}

export function getLimitedDimensions(width: number, height: number, optimizeDimensions: boolean, maxDimension: number)
{
    if (!optimizeDimensions || Math.max(width, height) <= maxDimension)
    {
        return { width, height };
    }

    const ratio = maxDimension / Math.max(width, height);

    return {
        width: Math.round(width * ratio),
        height: Math.round(height * ratio)
    };
}

export function resolveExportDimensions(params: ResolveExportDimensionsParams)
{
    if (params.outputResizeEnabled)
    {
        return getLimitedDimensions(params.outputSize.width, params.outputSize.height, params.optimizeDimensions, params.maxDimension);
    }

    if (params.mode === 'fixed-size')
    {
        return getLimitedDimensions(params.fixedSize.width, params.fixedSize.height, params.optimizeDimensions, params.maxDimension);
    }

    if (params.mode === 'template' && params.templateSize)
    {
        return getLimitedDimensions(params.templateSize.width, params.templateSize.height, params.optimizeDimensions, params.maxDimension);
    }

    if (params.mode === 'circular')
    {
        return getLimitedDimensions(params.circularSize, params.circularSize, params.optimizeDimensions, params.maxDimension);
    }

    return getLimitedDimensions(Math.round(params.cropWidth), Math.round(params.cropHeight), params.optimizeDimensions, params.maxDimension);
}

export function shouldPreprocessUploadImage(file: File)
{
    if (!file.type?.toLowerCase().startsWith('image/'))
        return false;

    if (file.type?.toLowerCase() === 'image/svg+xml')
        return false;

    return !file.name?.toLowerCase().endsWith('.svg');
}

export function resolveUploadExportFormat(file: File)
{
    if (file.type?.toLowerCase() === 'image/jpeg')
    {
        return 'jpeg';
    }

    if (file.type?.toLowerCase() === 'image/webp')
    {
        return 'webp';
    }

    return 'png';
}

export function buildPreprocessedUploadFilename(originalName: string, format: CanvasImageExportFormat)
{
    return buildEditedImageFilename(originalName, format, 'processed');
}

export async function preprocessUploadImage(file: File, options: UploadImagePreprocessOptions)
{
    if (!shouldPreprocessUploadImage(file))
    {
        return file;
    }

    const image = await loadImageFromFile(file);
    const format = resolveUploadExportFormat(file);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    canvas.width = image.width;
    canvas.height = image.height;

    if (format === 'jpeg')
    {
        context.fillStyle = '#fff';
        context.fillRect(0, 0, image.width, image.height);
    }

    context.drawImage(image, 0, 0, image.width, image.height);

    if (options.watermarkEnabled && options.watermarkType === 'logo' && options.watermarkLogoSrc)
    {
        const logo = await loadImageFromSrc(options.watermarkLogoSrc);
        const logoWidth = options.watermarkSize;
        const logoHeight = logoWidth * (logo.height / logo.width);
        const { x, y } = resolveWatermarkCenter(image.width, image.height, options.watermarkX, options.watermarkY, logoWidth, logoHeight);

        context.save();
        context.globalAlpha = options.watermarkOpacity / 100;
        context.drawImage(logo, x - logoWidth / 2, y - logoHeight / 2, logoWidth, logoHeight);
        context.restore();
    }
    else if (options.watermarkEnabled && options.watermarkText.trim().length > 0)
    {
        context.save();
        context.globalAlpha = options.watermarkOpacity / 100;
        context.font = `700 ${options.watermarkSize}px Arial`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.lineWidth = 4;
        context.strokeStyle = 'rgba(0, 0, 0, 0.55)';
        context.fillStyle = '#fff';

        const metrics = context.measureText(options.watermarkText);
        const textWidth = metrics.width + context.lineWidth;
        const textHeight = (metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent || options.watermarkSize) + context.lineWidth;
        const { x, y } = resolveWatermarkCenter(image.width, image.height, options.watermarkX, options.watermarkY, textWidth, textHeight);

        context.strokeText(options.watermarkText, x, y);
        context.fillText(options.watermarkText, x, y);
        context.restore();
    }

    const info = getExportFormatInfo(format);
    const blob = await new Promise<Blob>((resolve, reject) =>
    {
        canvas.toBlob((result) =>
        {
            if (result)
            {
                resolve(result);
            }
            else
            {
                reject(new Error('Image preprocessing failed.'));
            }
        }, info.mimeType, info.quality);
    });

    return new File([blob], buildPreprocessedUploadFilename(file.name, format), { type: blob.type || info.mimeType });
}

function loadImageFromFile(file: File)
{
    return new Promise((resolve, reject) =>
    {
        const reader = new FileReader();

        reader.addEventListener('load', () =>
        {
            const image = new Image();

            image.addEventListener('load', () => resolve(image), { once: true });
            image.addEventListener('error', reject, { once: true });
            image.src = reader.result as string;
        }, { once: true });
        reader.addEventListener('error', reject, { once: true });
        reader.readAsDataURL(file);
    });
}

function loadImageFromSrc(src: string)
{
    return new Promise((resolve, reject) =>
    {
        const image = new Image();

        image.addEventListener('load', () => resolve(image), { once: true });
        image.addEventListener('error', reject, { once: true });
        image.src = src;
    });
}
